/**
 * Owns the results loading state rendered by snippets/results-skeleton.liquid.
 *
 * Nosto renders into the mount from its own bundle and dispatches nothing, so the loaded signal is
 * the first child appearing in the mount. If nothing arrives at all the timeout removes the skeleton
 * anyway and reveals the fallback, so a dead provider never leaves the user staring at grey boxes.
 *
 * The skeleton itself is aria-hidden, so the fallback is announced through the live region too.
 */

// ponytail: 8s constant. Promote to a theme setting only if AU and NZ need different values.
const SKELETON_TIMEOUT_MS = 8000

const container = document.querySelector<HTMLElement>("[data-results-loading]")

if (container) {
    const skeleton = container.querySelector<HTMLElement>("[data-results-skeleton]")
    const fallback = container.querySelector<HTMLElement>("[data-results-fallback]")
    const status = container.querySelector<HTMLElement>("[data-results-status]")

    let observer: MutationObserver | undefined

    /**
     * The fallback already carries translated copy, so the announcement reuses it rather than
     * needing a string of its own. locales/*.json is gitignored and pulled per store, so a new key
     * would announce "Translation missing: ..." to screen reader users.
     */
    const reveal = () => {
        fallback?.classList.remove("hidden")

        const message = fallback?.querySelector("h1, h2, p")?.textContent
        if (status && message) {
            status.textContent = message.trim()
        }
    }

    const settle = () => {
        window.clearTimeout(timeoutId)
        observer?.disconnect()
        skeleton?.remove()
    }

    const timeoutId = window.setTimeout(() => {
        observer?.disconnect()
        skeleton?.remove()
        reveal()
        document.dispatchEvent(new CustomEvent("results:timeout"))
    }, SKELETON_TIMEOUT_MS)

    /**
     * Nosto renders into the provider mount from its own bundle, configured in the Nosto dashboard
     * rather than in this repo, so it never dispatches an event we can listen for. Without this the
     * timeout always won and the fallback was revealed underneath a full set of Nosto results.
     *
     * Nosto renders its own copy for zero results, so leaving the fallback hidden on this path is
     * deliberate: revealing it too would show two empty states at once.
     *
     * ponytail: first child is the loaded signal. Needs a content check only if Nosto starts
     * injecting an empty wrapper ahead of its results.
     */
    const mount = document.querySelector<HTMLElement>("#reactify-mount-search, #reactify-mount-collection")

    if (mount) {
        const settleIfPopulated = () => {
            if (mount.firstElementChild) {
                settle()
            }
        }

        observer = new MutationObserver(settleIfPopulated)
        observer.observe(mount, { childList: true })

        // Nosto may have injected before this module ran.
        settleIfPopulated()
    }
}
